// Utils
import { get } from 'lodash';
import { getHomeUrl } from './url';
import {
    ROUTE_CONFIRM_ACCOUNT,
    ROUTE_LOGIN,
    ROUTE_LOGIN_WITH_MFA,
    ROUTE_LOGIN_WITH_SSO,
    ROUTE_MFA_PHONE,
    ROUTE_RESET_PASSWORD,
    ROUTE_SEND_RESET_TOKEN,
    ROUTE_SSO_DIRECT_ACCESS,
    ROUTE_USERS,
    ROUTE_VALIDATE_ACCOUNT
} from './constants';

const AUTH_ROUTE_PATHS = [
    '/',
    ROUTE_CONFIRM_ACCOUNT,
    ROUTE_LOGIN,
    ROUTE_LOGIN_WITH_MFA,
    ROUTE_LOGIN_WITH_SSO,
    ROUTE_MFA_PHONE,
    ROUTE_RESET_PASSWORD,
    ROUTE_SEND_RESET_TOKEN,
    ROUTE_SSO_DIRECT_ACCESS,
    ROUTE_USERS,
    ROUTE_VALIDATE_ACCOUNT
];

/**
 * @typedef {object} RequestError
 * @property {import('axios').AxiosResponse} response the XHR response
 * @property {boolean} [isAxiosError] is this an axios error?
 * @property {string} [location] the URL where to redirect the browser
 * @property {number} [status] the response status (only for angular XHR calls)
 * @property {object} [data] the same as response.data
 */

/**
 * Parses a server error from a string.
 *
 * @param {import('axios').AxiosError} error - The Axios error string to parse.
 *
 * @returns {ParsedServerError} - The formatted error object.
 */
export function parseServerError(error) {
    const errorString = get(error, 'data.error');
    const genericError = { detail: 'generic_form.messages.save_failed' };

    if (typeof errorString !== 'string') {
        console.warn('[utils/net] Server did not returned an error string:', errorString);
        return genericError;
    }

    try {
        const parsedError = JSON.parse(errorString);
        if (parsedError.stack) {
            const stack = parsedError.stack.replaceAll('.appscript.vm', '').replaceAll(/:(\d+):(\d+)/g, ' at line $1:$2');
            console.error('Server responded with an error:', stack);
        } else {
            console.debug('[utils/net] JSON-Parsed server error', parsedError);
        }

        return parsedError;
    } catch (e) { /* Not a JSON string, continue */ }

    try {
        const [idField, id, message] = errorString.split(':').map(s => s.trim());
        const field = message?.substring(0, message.indexOf(' '));
        const detail = message?.substring(message.indexOf(' ') + 1);
        const parsedError = { idField, id, field, detail };

        if (!field && !detail && typeof id === 'string' && id.length && /\s/.test(id.trim())) {
            return id;
        }

        if (!parsedError.detail) {
            return genericError;
        }

        console.debug('[utils/net] Parsed server error', errorString, parsedError);
        return parsedError;

    } catch (e) {
        console.warn('[utils/net] Failed to parse server error', errorString, e);
        return { detail: errorString };
    }
}

/**
 * This method is used to intercept all requests that result
 * with a status code outside the range of 2xx.
 *
 * @param {RequestError} error the request error
 * @returns {Promise<void>}
 */
export function requestErrorInterceptor(error) {
    // unify error responses between axios and angular's $http
    if (error.isAxiosError) {
        error.data = get(error, 'response.data');
    }

    const status = error.status || get(error, 'response.status');

    switch (status) {
        case 400:
            return badRequestHandler(error);
        case 401:
            return unauthorizedHandler(error);
        case 303:
            return redirectHandler(error);

        default:
            return Promise.reject(error);
    }
}

/**
 * This handler is used to trigger an event on the window that will
 * then be intercepted by a proper component in the UI to display
 * the error details.
 *
 * @param {RequestError} error the request error
 * @returns {Promise<void>}
 */
async function badRequestHandler(error) {
    const data = get(error, 'response.data');

    if (data) {
        const event = new CustomEvent('bad-request', { detail: data });
        window.dispatchEvent(event);
    }
    return Promise.reject(error);
}

/**
 * This method is used to intercept all requests that result
 * with a 401 (unauthorized) status code.
 *
 * It attempts to check the session again and if session is
 * valid (and access to event), then it's a specific permission
 * issue.
 *
 * If the session check will return again a 401 then the user
 * have been un-authenticated. It will pass through this
 * code again, but this time it will have `config.isSessionCall`
 * set to true and user will be redirected to the login page.
 *
 * @param {RequestError} error the request error
 * @returns {Promise<void>}
 */
async function unauthorizedHandler(error) {
    try {
        if (get(error, 'config.isSessionCall')) {

            // This check prevents the storing of an unauthorized call
            // happening on the Auth application. We want to avoid this
            // because of the new backstage user's validation/activation.
            // In fact if the activation URL is expired or invalid, we
            // receive a 401 and if we store that invalid URL the user
            // won't be able to activate anymore.
            // See https://spotme.atlassian.net/browse/SS-20856
            const url = new URL(window.location.href);

            if (!isAuthRoute(url.pathname)) {
                const routerService = get(window, 'BSTG.services.router');
                routerService.saveRequestedUrl();
            }

            await logout();
        } else {
            const userService = get(window, 'BSTG.services.user');
            await userService.checkSession();
        }

    } finally {
        // eslint-disable-next-line no-unsafe-finally
        return Promise.reject(error);
    }
}

/**
 * Checks if the given path is an authentication route.
 *
 * @param {string} path - The path to check.
 *
 * @returns {boolean} - Returns true if the path is an authentication route, otherwise false.
 */
export function isAuthRoute(path) {
    return path.startsWith(ROUTE_LOGIN) || path.startsWith(ROUTE_USERS) || AUTH_ROUTE_PATHS.some(r => r === path);
}

/**
 * This method is used to intercept all requests that result
 * with a 303 (See Other) status code.
 *
 * @param {RequestError} error the request error
 */
async function redirectHandler(error) {
    window.location.href = getHomeUrl(error.location);
}

/**
 * Invalidates the user's session cookie and redirects to the
 * login page.
 */
async function logout() {
    try {
        await get(window, 'BSTG.services.session').logout();
    } finally {
        const url = new URL(window.location.href);
        if (!isAuthRoute(url.pathname)) {
            get(window, 'BSTG.services.router').redirectToHome(ROUTE_LOGIN);
        }
    }
}
